import { ReactNode } from "react";
import { MdLuggage, MdAirlineSeatReclineNormal } from "react-icons/md";
import { TbToolsKitchen2 } from "react-icons/tb";
import i18n from "src/i18n";
import { AirportType, BaggageOption, MealOption, PassengerInputType, SeatType } from "src/Modules/Flights/constants";


export type FlightRef = { flightNumber: string; origin: AirportType; destination: AirportType };

export type AggregatedMeal = MealOption & { quantity: number; totalPrice: number };
export type AggregatedSeat = SeatType & { quantity: number; totalPrice: number };
export type AggregatedBaggage = BaggageOption & { quantity: number; totalPrice: number };

export type SSRFlight<T> = { flight: FlightRef; items: T[] };

export type TransformedSegment = {
  direction: "Outbound" | "Inbound";
  meals: SSRFlight<AggregatedMeal>[];
  baggages: SSRFlight<AggregatedBaggage>[];
  seats: SSRFlight<AggregatedSeat>[];
};

export type TabKey = "seats" | "baggages" | "meals";

export type AddOnSummaryRow = { icon?: ReactNode; label: string; total: number; currency: string };

export type DisplayItem = { label: string; quantity: number; price: number; currency: string };

// ─── Utilities ────────────────────────────────────────────────────────────────

function aggregateByCode<T extends { code: string; price: number; quantity?: number }>(
  items: T[],
): (T & { quantity: number; totalPrice: number })[] {
  const map = new Map<string, T & { quantity: number; totalPrice: number }>();
  for (const item of items) {
    const qty = (item as any).quantity ?? 1;
    const existing = map.get(item.code);
    if (existing) {
      existing.quantity += qty;
      existing.totalPrice += item.price * qty;
    } else {
      map.set(item.code, { ...item, quantity: qty, totalPrice: item.price * qty });
    }
  }
  return Array.from(map.values());
}

export function transformPassengersToSegments(passengers: PassengerInputType): TransformedSegment[] {
  const segmentCount = passengers.passengers[0]?.segments.length ?? 0;

  return Array.from({ length: segmentCount }, (_, segIdx) => {
    const firstPaxSeg = passengers.passengers[0].segments[segIdx];

    const meals: SSRFlight<AggregatedMeal>[] = [];
    const seats: SSRFlight<AggregatedSeat>[] = [];
    const baggages: SSRFlight<AggregatedBaggage>[] = [];

    firstPaxSeg.flights.forEach((flightWrapper, fIdx) => {
      const flight = flightWrapper.flight;
      const flightRef: FlightRef = {
        flightNumber: flight.flightNumber,
        origin: flight.origin,
        destination: flight.destination,
      };

      const rawMeals: MealOption[] = [];
      const rawSeats: SeatType[] = [];

      passengers.passengers.forEach((pax) => {
        const paxFlight = pax.segments[segIdx]?.flights[fIdx];
        if (!paxFlight) return;
        if (paxFlight.ssr?.meals) rawMeals.push(...Object.values(paxFlight.ssr.meals));
        if (paxFlight.ssr?.seats) rawSeats.push(...Object.values(paxFlight.ssr.seats));
      });

      if (rawMeals.length > 0) meals.push({ flight: flightRef, items: aggregateByCode(rawMeals) });
      if (rawSeats.length > 0) seats.push({ flight: flightRef, items: aggregateByCode(rawSeats) });
    });

    const baggageMap = new Map<string, { flightRef: FlightRef; items: BaggageOption[] }>();
    passengers.passengers.forEach((pax) => {
      const paxSeg = pax.segments[segIdx];
      if (!paxSeg?.ssr?.baggages) return;
      Object.values(paxSeg.ssr.baggages).forEach((b) => {
        const key = `${b.origin}|${b.destination}`;
        if (!baggageMap.has(key)) {
          const allFlights = firstPaxSeg.flights.map((f) => f.flight);
          const findAirport = (code: string): AirportType =>
            allFlights.find((f) => f.origin.code === code)?.origin ??
            allFlights.find((f) => f.destination.code === code)?.destination ?? {
              code,
              name: code,
              terminal: "",
              cityName: code,
              countryName: "",
            };
          baggageMap.set(key, {
            flightRef: { flightNumber: b.flightNumber, origin: findAirport(b.origin), destination: findAirport(b.destination) },
            items: [],
          });
        }
        baggageMap.get(key)!.items.push(b);
      });
    });

    for (const { flightRef, items } of baggageMap.values()) {
      baggages.push({ flight: flightRef, items: aggregateByCode(items) });
    }

    return {
      direction: segIdx === 0 ? "Outbound" : "Inbound",
      meals,
      seats,
      baggages,
    };
  });
}

export function hasAnySSR(segments: TransformedSegment[]): boolean {
  return segments.some((seg) => seg.meals.length > 0 || seg.baggages.length > 0 || seg.seats.length > 0);
}

export function buildAddOnSummaryFromSegments(segments: TransformedSegment[]): AddOnSummaryRow[] {
  let mealTotal = 0, seatTotal = 0, baggageTotal = 0;
  let currency = "";

  for (const seg of segments) {
    for (const sf of seg.meals) {
      for (const item of sf.items) { mealTotal += item.totalPrice; currency = item.currency; }
    }
    for (const sf of seg.seats) {
      for (const item of sf.items) { seatTotal += item.totalPrice; currency = item.currency; }
    }
    for (const sf of seg.baggages) {
      for (const item of sf.items) { baggageTotal += item.totalPrice; currency = item.currency; }
    }
  }

  const rows: AddOnSummaryRow[] = [];
  if (mealTotal > 0 || segments.some((s) => s.meals.length > 0))
    rows.push({ icon: <TbToolsKitchen2 size={16} />, label: i18n.t("Flights.details.summary.mealLabel"), total: mealTotal, currency });
  if (seatTotal > 0 || segments.some((s) => s.seats.length > 0))
    rows.push({ icon: <MdAirlineSeatReclineNormal size={16} />, label: i18n.t("Flights.details.summary.seatLabel"), total: seatTotal, currency });
  if (baggageTotal > 0 || segments.some((s) => s.baggages.length > 0))
    rows.push({ icon: <MdLuggage size={16} />, label: i18n.t("Flights.details.summary.baggageLabel"), total: baggageTotal, currency });
  return rows;
}

export function toDisplayItems(tab: TabKey, ssrFlight: SSRFlight<AggregatedMeal | AggregatedBaggage | AggregatedSeat>): DisplayItem[] {
  if (tab === "meals") {
    return (ssrFlight as SSRFlight<AggregatedMeal>).items.map((m) => ({
      label: m.airlineDescription ?? m.code,
      quantity: m.quantity,
      price: m.price,
      currency: m.currency,
    }));
  }
  if (tab === "baggages") {
    return (ssrFlight as SSRFlight<AggregatedBaggage>).items.map((b) => ({
      label: i18n.t("Flights.details.summary.weightKg", { weight: b.weight }),
      quantity: b.quantity,
      price: b.price,
      currency: b.currency,
    }));
  }
  return (ssrFlight as SSRFlight<AggregatedSeat>).items.map((s) => ({
    label: s.seatNo ? i18n.t("Flights.details.summary.seatNumber", { seatNo: s.seatNo }) : s.code,
    quantity: s.quantity,
    price: s.price,
    currency: s.currency,
  }));
}
